import type { ReactNode } from 'react';

import { OrnamentalRule } from './OrnateFrame.js';

/**
 * The heading every *plate* region in the app shares — the profile's "Overall"
 * and "Gameplay" (APP_SPEC §13, §13.3) and the Games page's "Continue Playing"
 * and "Start a Game" (approved Games-page mockup): a subject glyph, the title
 * set in the plate's Alegreya at bold, then a gold rule carrying a diamond
 * ornament and fading out to the right.
 *
 * Lifted here from `features/profile` so the two pages cannot drift apart
 * typographically — the Games page previously used `SectionHeading`
 * (`Layout.tsx`), whose 1.25rem Source Serif title and bare CSS hairline are a
 * visibly lighter treatment, which is what made the catalog read as a
 * different design system from the profile it sits beside.
 *
 * `SectionHeading` deliberately remains for the list screens (Notifications,
 * Friends, Admin, Settings): those are dense editorial lists, not illustrated
 * plates, and they are not part of this shared treatment.
 *
 * The rule and its diamond are decoration only — the heading text is the whole
 * accessible name.
 */
export interface PlateSectionHeadingProps {
  /** The glyph before the title. Sized by the caller. */
  icon: ReactNode;
  title: string;
  /**
   * `page` = the dominant heading on a page whose plate carries only two or
   * three of them (the Games catalog); `section` = a page-level region heading
   * among many (the profile); `sub` = one nested inside a plate.
   */
  size?: 'page' | 'section' | 'sub';
  /** Heading level, so each page keeps a correct heading order. */
  as?: 'h2' | 'h3';
  /** Optional trailing control (e.g. "View all"), kept outside the rule. */
  action?: ReactNode;
  className?: string;
}

export function PlateSectionHeading({
  icon,
  title,
  size = 'section',
  as: Heading = 'h2',
  action,
  className = '',
}: PlateSectionHeadingProps) {
  // Every size is set in the plate's Alegreya at bold; they differ only in
  // scale. `sub` is only a step below `section` rather than the small
  // letter-spaced label it used to be, because a heading inside a plate is
  // still a real section heading. `page` is a step *above* it, for a plate
  // holding two or three headings that each introduce a whole region — at
  // `section` those read as captions rather than as the divisions of the page
  // they actually are.
  const TITLE_CLASSES: Record<'page' | 'section' | 'sub', string> = {
    page: 'font-profile text-[1.85rem] font-bold leading-tight tablet:text-[2.05rem]',
    section: 'font-profile text-[1.7rem] font-bold leading-tight tablet:text-[1.9rem]',
    sub: 'font-profile text-[1.55rem] font-bold leading-tight tablet:text-[1.75rem]',
  };
  const titleClass = TITLE_CLASSES[size];

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {icon}
      <Heading className={`shrink-0 text-textPrimary ${titleClass}`}>{title}</Heading>
      <OrnamentalRule className="min-w-0 flex-1" />
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
